import baseEntries from './base.json' with { type: 'json' };
import type {
  NewsArticle,
  NewsLocale,
  NewsOverrides,
  NewsEntry,
  NewsTranslations,
} from './types';
export type {
  NewsArticle,
  NewsLocale,
  NewsOverrides,
  NewsEntry,
  NewsTranslations,
} from './types';
export { fetchRemoteNewsOverrides, type NewsSite } from './remote';
export { serializeJsonForHtml } from './serialize-json';

const SUPPORTED_LOCALES: NewsLocale[] = ['ko', 'en'];
const BASE_ENTRIES = baseEntries as NewsEntry[];

function buildBaseArticles(locale: NewsLocale): NewsArticle[] {
  return BASE_ENTRIES.flatMap(entry => {
    const localized = resolveTranslation(entry.translations, locale);
    if (!localized) {
      return [];
    }

    return [
      {
        id: entry.id,
        date: entry.date,
        thumbnail: entry.thumbnail,
        tag: entry.tag,
        title: localized.title,
        content: localized.content,
      },
    ];
  });
}

function resolveTranslation(
  translations: NewsTranslations,
  locale: NewsLocale,
) {
  return translations[locale] ?? translations.ko ?? translations.en ?? null;
}

function mergeArticles(
  baseArticles: NewsArticle[],
  overrides: NewsArticle[] = [],
) {
  const map = new Map<number, NewsArticle>();

  for (const article of baseArticles) {
    map.set(article.id, article);
  }

  for (const override of overrides) {
    const existing = map.get(override.id);
    if (existing) {
      map.set(override.id, {
        ...existing,
        ...override,
        thumbnail: override.thumbnail?.length
          ? override.thumbnail
          : existing.thumbnail,
        tag: override.tag ?? existing.tag,
      });
    } else {
      map.set(override.id, override);
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    const dateA = a.date?.replace(/\./g, '-') ?? '';
    const dateB = b.date?.replace(/\./g, '-') ?? '';
    if (dateA !== dateB) {
      return dateB.localeCompare(dateA);
    }
    return b.id - a.id;
  });
}

const BASE_DATA = SUPPORTED_LOCALES.reduce<Record<NewsLocale, NewsArticle[]>>(
  (acc, locale) => {
    acc[locale] = buildBaseArticles(locale);
    return acc;
  },
  {} as Record<NewsLocale, NewsArticle[]>,
);

export function getBaseNews(): Record<NewsLocale, NewsArticle[]> {
  return {
    ko: [...BASE_DATA.ko],
    en: [...BASE_DATA.en],
  };
}

export function getNewsData(
  overrides: NewsOverrides = {},
): Record<NewsLocale, NewsArticle[]> {
  return SUPPORTED_LOCALES.reduce<Record<NewsLocale, NewsArticle[]>>(
    (acc, locale) => {
      const baseArticles = BASE_DATA[locale] ?? [];
      const overrideArticles = overrides[locale] ?? [];
      acc[locale] = mergeArticles(baseArticles, overrideArticles);
      return acc;
    },
    {} as Record<NewsLocale, NewsArticle[]>,
  );
}
